import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["container", "input", "dropdown", "results", "result", "backdrop", "searchWrapper", "trigger"];
  static values = { url: String };

  connect() {
    this.handleKeydown = this.#globalKeydown.bind(this);
    this.handleClickOutside = this.#clickOutside.bind(this);
    document.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("click", this.handleClickOutside);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("click", this.handleClickOutside);
  }

  open() {
    this.#showBackdrop();
    this.inputTarget.focus();
    this.inputTarget.select();
  }

  showDropdown() {
    this.#showBackdrop();
    if (this.inputTarget.value.trim() !== "") {
      this.#showResults();
    }
  }

  closeDropdown() {
    clearTimeout(this.timeout);
    this.dropdownTarget.classList.add("hidden");
    this.backdropTarget.classList.add("hidden");
    this.searchWrapperTarget.classList.remove("z-30");
    this.searchWrapperTarget.classList.add("z-10");
    this.#clearActiveResult();
  }

  #showBackdrop() {
    this.backdropTarget.classList.remove("hidden");
    this.searchWrapperTarget.classList.remove("z-10");
    this.searchWrapperTarget.classList.add("z-30");
  }

  #showResults() {
    this.dropdownTarget.classList.remove("hidden");
  }

  search() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.#fetchResults(), 250);
  }

  keydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.#moveSelection(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.#moveSelection(-1);
        break;
      case "Enter":
        event.preventDefault();
        this.#openSelected();
        break;
      case "Escape":
        this.closeDropdown();
        this.inputTarget.blur();
        break;
    }
  }

  #globalKeydown(event) {
    if (document.querySelector("dialog[open]")) return;

    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      this.open();
    }

    if (event.key === "/" && !event.metaKey && !event.ctrlKey && !this.#isInputElement(event.target)) {
      event.preventDefault();
      this.open();
    }
  }

  #clickOutside(event) {
    if (this.containerTarget.contains(event.target)) return;
    if (this.triggerTargets.some((trigger) => trigger.contains(event.target))) return;

    this.closeDropdown();
  }

  #fetchResults() {
    const query = this.inputTarget.value.trim();
    if (query === "") {
      this.resultsTarget.innerHTML = "";
      this.closeDropdown();
      return;
    }

    this.#showBackdrop();
    this.#showResults();
    const url = `${this.urlValue}?query=${encodeURIComponent(query)}`;
    this.resultsTarget.src = url;
  }

  #moveSelection(direction) {
    const results = this.resultTargets;
    if (results.length === 0) return;

    const current = results.findIndex((el) => el === this.activeResult);
    const next = Math.max(0, Math.min(results.length - 1, current + direction));

    this.#setActiveResult(results[next]);
  }

  #setActiveResult(element) {
    this.#clearActiveResult();
    this.activeResult = element;
    element.classList.add("bg-fill-elevation-level-base");
    element.scrollIntoView({ block: "nearest" });
  }

  #clearActiveResult() {
    this.resultTargets.forEach((el) => el.classList.remove("bg-fill-elevation-level-base"));
    this.activeResult = null;
  }

  #openSelected() {
    if (this.activeResult) {
      this.activeResult.click();
    }
  }

  #isInputElement(element) {
    const tagName = element.tagName;
    return tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT" || element.isContentEditable;
  }
}
